import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeftIcon, 
  DocumentTextIcon,
  UserPlusIcon,
  ClockIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CalendarDaysIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BanknotesIcon,
  InformationCircleIcon,
  PlusIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiHelper';
import { toast } from '../../utils/notification';
import { formatCurrency, formatDate, formatHours } from '../../utils';

// ─── Quick Add Client Modal ────────────────────────────────────────────────────
const QuickAddClientModal = ({ isOpen, onClose, onClientCreated }) => {
  const [saving, setSaving] = useState(false);
  const [clientForm, setClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
    if (isOpen) {
      setClientForm({ first_name: '', last_name: '', email: '', phone: '', address: '', notes: '' });
      setErrors({});
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!clientForm.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!clientForm.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!clientForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientForm.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const newClient = await apiRequest('/api/clients/', 'POST', clientForm);
      toast.success(`Client "${newClient.first_name} ${newClient.last_name}" created`);
      onClientCreated(newClient);
      onClose();
    } catch (err) {
      console.error('Failed to create client:', err);
      if (err?.response) {
        const serverErrors = {};
        const responseData = typeof err.response === 'object' ? err.response : {};
        Object.entries(responseData).forEach(([key, val]) => {
          serverErrors[key] = Array.isArray(val) ? val.join(', ') : String(val);
        });
        // Check for duplicate email specifically
        if (serverErrors.email && serverErrors.email.toLowerCase().includes('already exists')) {
          serverErrors.email = 'A client with this email already exists. Please use a different email or select the existing client.';
        }
        setErrors(serverErrors);
      } else {
        toast.error('Failed to create client. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = (field) =>
    `mt-1 block w-full rounded-lg border ${
      errors[field]
        ? 'border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
    } shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5 transition-colors`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="quick-add-client" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
        
        {/* Modal */}
        <div className="relative w-full max-w-lg transform rounded-xl bg-white dark:bg-gray-800 shadow-2xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                <UserPlusIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Client</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Quick-add and auto-select for this invoice</p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-500 transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    value={clientForm.first_name}
                    onChange={(e) => setClientForm({ ...clientForm, first_name: e.target.value })}
                    className={inputCls('first_name')}
                    placeholder="John"
                  />
                  {errors.first_name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={clientForm.last_name}
                    onChange={(e) => setClientForm({ ...clientForm, last_name: e.target.value })}
                    className={inputCls('last_name')}
                    placeholder="Doe"
                  />
                  {errors.last_name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.last_name}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  className={inputCls('email')}
                  placeholder="john.doe@example.com"
                />
                {errors.email && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                <input
                  type="tel"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  className={inputCls('phone')}
                  placeholder="+263 77 123 4567"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors shadow-sm"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <UserPlusIcon className="h-4 w-4 mr-1.5" />
                    Add Client
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ─── Stepper Badge ─────────────────────────────────────────────────────────────
const StepBadge = ({ number, label, active, completed }) => (
  <div className="flex items-center gap-2.5">
    <div
      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
        completed
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : active
          ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/30'
          : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
      }`}
    >
      {completed ? <CheckCircleIcon className="h-5 w-5" /> : number}
    </div>
    <span
      className={`text-sm font-medium ${
        active ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
      }`}
    >
      {label}
    </span>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const CreateInvoice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialClientId = searchParams.get('client');
  
  const [loading, setLoading] = useState(false);
  const [fetchingItems, setFetchingItems] = useState(false);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [selectedTimeEntries, setSelectedTimeEntries] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddTimeEntry, setShowAddTimeEntry] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [savingTimeEntry, setSavingTimeEntry] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [newTimeEntry, setNewTimeEntry] = useState({
    case: '', date: new Date().toISOString().split('T')[0], hours: '', description: '', rate: '',
  });
  const [newExpense, setNewExpense] = useState({
    case: '', date: new Date().toISOString().split('T')[0], description: '', amount: '',
  });
  const [formData, setFormData] = useState({
    client: initialClientId || '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
  });
  const [error, setError] = useState(null);

  const fetchClients = async () => {
    try {
      const data = await apiRequest('/api/clients/');
      setClients(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setError('Failed to load clients. Please try again.');
    }
  };

  const fetchUnbilledItems = useCallback(async (clientId) => {
    if (!clientId) return;
    
    setFetchingItems(true);
    try {
      const [timeEntriesRaw, expensesRaw, casesRaw] = await Promise.all([
        apiRequest(`/api/billing/time-entries/?client_id=${clientId}&invoiced=false&billable=true`),
        apiRequest(`/api/billing/expenses/?client_id=${clientId}&invoiced=false&billable=true`),
        apiRequest(`/api/cases/?client_id=${clientId}`),
      ]);
      const timeEntriesData = Array.isArray(timeEntriesRaw) ? timeEntriesRaw : timeEntriesRaw?.results || [];
      setTimeEntries(timeEntriesData);
      
      const expensesData = Array.isArray(expensesRaw) ? expensesRaw : expensesRaw?.results || [];
      setExpenses(expensesData);

      const casesData = Array.isArray(casesRaw) ? casesRaw : casesRaw?.results || [];
      setCases(casesData);
      
      setSelectedTimeEntries(timeEntriesData.map(entry => entry.id));
      setSelectedExpenses(expensesData.map(expense => expense.id));
    } catch (err) {
      console.error('Failed to fetch unbilled items:', err);
      setError('Failed to load unbilled items. Please try again.');
    } finally {
      setFetchingItems(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    if (initialClientId) {
      fetchUnbilledItems(initialClientId);
    }
  }, [initialClientId, fetchUnbilledItems]);

  // Derived totals
  const totalAmount = useMemo(() => {
    const timeTotal = timeEntries
      .filter(e => selectedTimeEntries.includes(e.id))
      .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0);
    const expTotal = expenses
      .filter(e => selectedExpenses.includes(e.id))
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    return timeTotal + expTotal;
  }, [timeEntries, expenses, selectedTimeEntries, selectedExpenses]);

  const timeSubtotal = useMemo(() => {
    return timeEntries
      .filter(e => selectedTimeEntries.includes(e.id))
      .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0);
  }, [timeEntries, selectedTimeEntries]);

  const expenseSubtotal = useMemo(() => {
    return expenses
      .filter(e => selectedExpenses.includes(e.id))
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  }, [expenses, selectedExpenses]);

  const selectedClient = useMemo(() => {
    return clients.find(c => String(c.id) === String(formData.client));
  }, [clients, formData.client]);

  // Filtered clients for search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      (c.email && c.email.toLowerCase().includes(q))
    );
  }, [clients, clientSearch]);

  // Step tracking
  const currentStep = !formData.client ? 1 : (selectedTimeEntries.length === 0 && selectedExpenses.length === 0) ? 2 : 3;

  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    setFormData({ ...formData, client: clientId });
    setSelectedTimeEntries([]);
    setSelectedExpenses([]);
    setTimeEntries([]);
    setExpenses([]);
    setCases([]);
    setClientSearch('');
    setShowAddTimeEntry(false);
    setShowAddExpense(false);
    
    if (clientId) {
      fetchUnbilledItems(clientId);
    }
  };

  const handleClientCreated = (newClient) => {
    setClients(prev => [...prev, newClient]);
    setFormData(prev => ({ ...prev, client: String(newClient.id) }));
    setSelectedTimeEntries([]);
    setSelectedExpenses([]);
    setTimeEntries([]);
    setExpenses([]);
    setCases([]);
    fetchUnbilledItems(newClient.id);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTimeEntrySelection = (id) => {
    setSelectedTimeEntries(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const handleExpenseSelection = (id) => {
    setSelectedExpenses(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (type) => {
    if (type === 'time') {
      setSelectedTimeEntries(prev =>
        prev.length === timeEntries.length ? [] : timeEntries.map(e => e.id)
      );
    } else {
      setSelectedExpenses(prev =>
        prev.length === expenses.length ? [] : expenses.map(e => e.id)
      );
    }
  };

  const handleAddTimeEntry = async () => {
    if (!newTimeEntry.case || !newTimeEntry.hours || !newTimeEntry.description || !newTimeEntry.rate) return;
    setSavingTimeEntry(true);
    try {
      const created = await apiRequest('/api/billing/time-entries/', 'POST', {
        case: parseInt(newTimeEntry.case),
        user: user?.id,
        date: newTimeEntry.date,
        hours: newTimeEntry.hours,
        description: newTimeEntry.description,
        rate: newTimeEntry.rate,
        is_billable: true,
      });
      setTimeEntries(prev => [...prev, created]);
      setSelectedTimeEntries(prev => [...prev, created.id]);
      setNewTimeEntry({ case: '', date: new Date().toISOString().split('T')[0], hours: '', description: '', rate: '' });
      setShowAddTimeEntry(false);
      toast.success('Time entry added');
    } catch (err) {
      console.error('Failed to create time entry:', err);
      const detail = err?.response ? Object.values(err.response).flat().join('; ') : '';
      toast.error(detail || 'Failed to add time entry');
    } finally {
      setSavingTimeEntry(false);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.case || !newExpense.description || !newExpense.amount) return;
    setSavingExpense(true);
    try {
      const created = await apiRequest('/api/billing/expenses/', 'POST', {
        case: parseInt(newExpense.case),
        date: newExpense.date,
        description: newExpense.description,
        amount: newExpense.amount,
        is_billable: true,
      });
      setExpenses(prev => [...prev, created]);
      setSelectedExpenses(prev => [...prev, created.id]);
      setNewExpense({ case: '', date: new Date().toISOString().split('T')[0], description: '', amount: '' });
      setShowAddExpense(false);
      toast.success('Expense added');
    } catch (err) {
      console.error('Failed to create expense:', err);
      const detail = err?.response ? Object.values(err.response).flat().join('; ') : '';
      toast.error(detail || 'Failed to add expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client) {
      setError('Please select a client');
      return;
    }
    
    if (selectedTimeEntries.length === 0 && selectedExpenses.length === 0) {
      setError('Please select at least one time entry or expense');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const invoiceData = await apiRequest('/api/billing/invoices/', 'POST', formData);
      
      if (selectedTimeEntries.length > 0) {
        await apiRequest(`/api/billing/invoices/${invoiceData.id}/add_time_entries/`, 'POST', {
          time_entry_ids: selectedTimeEntries
        });
      }
      
      if (selectedExpenses.length > 0) {
        await apiRequest(`/api/billing/invoices/${invoiceData.id}/add_expenses/`, 'POST', {
          expense_ids: selectedExpenses
        });
      }
      
      toast.success('Invoice created successfully');
      navigate(`/billing/invoices/${invoiceData.id}`);
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setError('Failed to create invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.client && (selectedTimeEntries.length > 0 || selectedExpenses.length > 0);

  return (
    <div className="max-w-7xl mx-auto">
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div className="mb-6">
        <Link to="/billing/invoices" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1.5" /> Back to Invoices
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Invoice</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Select a client, choose billable items, and generate an invoice.</p>
          </div>
          {/* Progress Steps */}
          <div className="flex items-center gap-4">
            <StepBadge number={1} label="Client" active={currentStep === 1} completed={currentStep > 1} />
            <div className="h-px w-6 bg-gray-300 dark:bg-gray-600" />
            <StepBadge number={2} label="Items" active={currentStep === 2} completed={currentStep > 2} />
            <div className="h-px w-6 bg-gray-300 dark:bg-gray-600" />
            <StepBadge number={3} label="Review" active={currentStep === 3} completed={false} />
          </div>
        </div>
      </div>

      {/* ─── Error Banner ────────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 mb-6 flex items-start gap-3">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ─── Main Layout: Form + Sidebar ─────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Main Form */}
        <div className="flex-1 min-w-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* ── Section 1: Client & Invoice Details ─────────────────── */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/80">
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Client & Invoice Details</h3>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {/* Client Selector Row */}
                <div>
                  <label htmlFor="client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      {/* Search filter above select */}
                      {clients.length > 5 && (
                        <div className="mb-2 relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search clients..."
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            className="block w-full rounded-lg border-gray-300 dark:border-gray-600 pl-9 pr-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      )}
                      <select
                        id="client"
                        name="client"
                        value={formData.client}
                        onChange={handleClientChange}
                        required
                        className="block w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm dark:bg-gray-700 dark:text-white py-2.5 px-3"
                      >
                        <option value="">-- Select a client --</option>
                        {Array.isArray(filteredClients) && filteredClients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.first_name} {client.last_name} {client.email ? `(${client.email})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddClientModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors whitespace-nowrap"
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      New Client
                    </button>
                  </div>
                  {selectedClient && (
                    <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/30 px-3 py-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-xs">
                        {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {selectedClient.first_name} {selectedClient.last_name}
                        </span>
                        {selectedClient.email && (
                          <span className="text-gray-500 dark:text-gray-400 ml-2">{selectedClient.email}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Dates & Invoice Number */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Invoice #</label>
                    <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                      <DocumentDuplicateIcon className="h-4 w-4 mr-2 text-gray-400" />
                      Auto-generated
                    </div>
                  </div>
                  <div>
                    <label htmlFor="issue_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Issue Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="date"
                        id="issue_date"
                        name="issue_date"
                        value={formData.issue_date}
                        onChange={handleFormChange}
                        required
                        className="block w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm dark:bg-gray-700 dark:text-white pl-9 py-2.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="date"
                        id="due_date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleFormChange}
                        required
                        className="block w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm dark:bg-gray-700 dark:text-white pl-9 py-2.5"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows="2"
                    value={formData.notes}
                    onChange={handleFormChange}
                    className="block w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5"
                    placeholder="Payment terms, instructions, or additional notes..."
                  />
                </div>
              </div>
            </div>

            {/* ── Section 2: Time Entries ──────────────────────────────── */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Time Entries</h3>
                  {timeEntries.length > 0 && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                      {selectedTimeEntries.length}/{timeEntries.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {timeEntries.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSelectAll('time')}
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                    >
                      {selectedTimeEntries.length === timeEntries.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                  {formData.client && (
                    <button
                      type="button"
                      onClick={() => setShowAddTimeEntry(prev => !prev)}
                      className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                    >
                      {showAddTimeEntry ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
                      {showAddTimeEntry ? 'Cancel' : 'Add Entry'}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="p-6">
                {/* Inline Add Time Entry Form */}
                {showAddTimeEntry && formData.client && (
                  <div className="mb-5 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">New Time Entry</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Case <span className="text-red-500">*</span></label>
                        <select
                          value={newTimeEntry.case}
                          onChange={e => setNewTimeEntry({ ...newTimeEntry, case: e.target.value })}
                          className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3"
                        >
                          <option value="">-- Select case --</option>
                          {cases.map(c => (
                            <option key={c.id} value={c.id}>{c.title || c.case_number || `Case #${c.id}`}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</label>
                        <input type="date" value={newTimeEntry.date} onChange={e => setNewTimeEntry({ ...newTimeEntry, date: e.target.value })}
                          className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Hours <span className="text-red-500">*</span></label>
                        <input type="number" step="0.25" min="0.25" value={newTimeEntry.hours} onChange={e => setNewTimeEntry({ ...newTimeEntry, hours: e.target.value })}
                          placeholder="e.g. 1.5" className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rate ($/hr) <span className="text-red-500">*</span></label>
                        <input type="number" step="0.01" min="0" value={newTimeEntry.rate} onChange={e => setNewTimeEntry({ ...newTimeEntry, rate: e.target.value })}
                          placeholder="e.g. 250.00" className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description <span className="text-red-500">*</span></label>
                      <input type="text" value={newTimeEntry.description} onChange={e => setNewTimeEntry({ ...newTimeEntry, description: e.target.value })}
                        placeholder="Brief description of work performed" className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button type="button" onClick={() => setShowAddTimeEntry(false)}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                        Cancel
                      </button>
                      <button type="button" onClick={handleAddTimeEntry} disabled={savingTimeEntry || !newTimeEntry.case || !newTimeEntry.hours || !newTimeEntry.description || !newTimeEntry.rate}
                        className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors">
                        {savingTimeEntry ? 'Saving...' : <><PlusIcon className="h-3.5 w-3.5" /> Add Entry</>}
                      </button>
                    </div>
                    {cases.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">No cases found for this client. Create a case first to add time entries.</p>
                    )}
                  </div>
                )}

                {fetchingItems ? (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading billable items...
                  </div>
                ) : !formData.client ? (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                    <UserIcon className="h-10 w-10 mb-2 opacity-50" />
                    <p className="text-sm">Select a client to view unbilled time entries</p>
                  </div>
                ) : timeEntries.length === 0 && !showAddTimeEntry ? (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                    <ClockIcon className="h-10 w-10 mb-2 opacity-50" />
                    <p className="text-sm font-medium">No unbilled time entries</p>
                    <p className="text-xs mt-1">This client has no billable time entries pending</p>
                    <button type="button" onClick={() => setShowAddTimeEntry(true)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors">
                      <PlusIcon className="h-4 w-4" /> Add Time Entry
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-6">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700/50">
                          <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 w-10"></th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff</th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Hours</th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Rate</th>
                          <th className="px-6 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-700/30">
                        {timeEntries.map(entry => {
                          const isSelected = selectedTimeEntries.includes(entry.id);
                          return (
                            <tr
                              key={entry.id}
                              className={`cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-primary-50/60 dark:bg-primary-900/10 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                              }`}
                              onClick={() => handleTimeEntrySelection(entry.id)}
                            >
                              <td className="px-6 py-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTimeEntrySelection(entry.id)}
                                  className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-500 transition-colors"
                                />
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                {formatDate(entry.date)}
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-200">
                                <div className="max-w-xs truncate" title={entry.description}>{entry.description}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                {entry.user_detail ? `${entry.user_detail.first_name} ${entry.user_detail.last_name}` : 'Unknown'}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 text-right tabular-nums">
                                {formatHours(entry.hours)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 text-right tabular-nums">
                                {formatCurrency(entry.rate)}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right tabular-nums">
                                {formatCurrency(entry.amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {selectedTimeEntries.length > 0 && (
                        <tfoot>
                          <tr className="border-t border-gray-200 dark:border-gray-700">
                            <td colSpan={6} className="px-6 py-2.5 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                              Time Subtotal
                            </td>
                            <td className="px-6 py-2.5 text-right text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                              {formatCurrency(timeSubtotal)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* ── Section 3: Expenses ─────────────────────────────────── */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BanknotesIcon className="h-5 w-5 text-gray-400" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Expenses</h3>
                  {expenses.length > 0 && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                      {selectedExpenses.length}/{expenses.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {expenses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSelectAll('expense')}
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                    >
                      {selectedExpenses.length === expenses.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                  {formData.client && (
                    <button
                      type="button"
                      onClick={() => setShowAddExpense(prev => !prev)}
                      className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                    >
                      {showAddExpense ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
                      {showAddExpense ? 'Cancel' : 'Add Expense'}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="p-6">
                {/* Inline Add Expense Form */}
                {showAddExpense && formData.client && (
                  <div className="mb-5 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">New Expense</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Case <span className="text-red-500">*</span></label>
                        <select
                          value={newExpense.case}
                          onChange={e => setNewExpense({ ...newExpense, case: e.target.value })}
                          className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3"
                        >
                          <option value="">-- Select case --</option>
                          {cases.map(c => (
                            <option key={c.id} value={c.id}>{c.title || c.case_number || `Case #${c.id}`}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</label>
                        <input type="date" value={newExpense.date} onChange={e => setNewExpense({ ...newExpense, date: e.target.value })}
                          className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description <span className="text-red-500">*</span></label>
                        <input type="text" value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })}
                          placeholder="e.g. Filing fees, Travel costs" className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Amount ($) <span className="text-red-500">*</span></label>
                        <input type="number" step="0.01" min="0" value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })}
                          placeholder="e.g. 150.00" className="block w-full rounded-lg border-gray-300 dark:border-gray-600 text-sm dark:bg-gray-700 dark:text-white py-2 px-3" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button type="button" onClick={() => setShowAddExpense(false)}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                        Cancel
                      </button>
                      <button type="button" onClick={handleAddExpense} disabled={savingExpense || !newExpense.case || !newExpense.description || !newExpense.amount}
                        className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors">
                        {savingExpense ? 'Saving...' : <><PlusIcon className="h-3.5 w-3.5" /> Add Expense</>}
                      </button>
                    </div>
                    {cases.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">No cases found for this client. Create a case first to add expenses.</p>
                    )}
                  </div>
                )}

                {fetchingItems ? (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading expenses...
                  </div>
                ) : !formData.client ? (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                    <UserIcon className="h-10 w-10 mb-2 opacity-50" />
                    <p className="text-sm">Select a client to view unbilled expenses</p>
                  </div>
                ) : expenses.length === 0 && !showAddExpense ? (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                    <BanknotesIcon className="h-10 w-10 mb-2 opacity-50" />
                    <p className="text-sm font-medium">No unbilled expenses</p>
                    <p className="text-xs mt-1">This client has no billable expenses pending</p>
                    <button type="button" onClick={() => setShowAddExpense(true)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors">
                      <PlusIcon className="h-4 w-4" /> Add Expense
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-6">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700/50">
                          <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 w-10"></th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                          <th className="px-6 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-700/30">
                        {expenses.map(expense => {
                          const isSelected = selectedExpenses.includes(expense.id);
                          return (
                            <tr
                              key={expense.id}
                              className={`cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-primary-50/60 dark:bg-primary-900/10 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                              }`}
                              onClick={() => handleExpenseSelection(expense.id)}
                            >
                              <td className="px-6 py-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleExpenseSelection(expense.id)}
                                  className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-500 transition-colors"
                                />
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                {formatDate(expense.date)}
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-200">
                                {expense.description}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right tabular-nums">
                                {formatCurrency(expense.amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {selectedExpenses.length > 0 && (
                        <tfoot>
                          <tr className="border-t border-gray-200 dark:border-gray-700">
                            <td colSpan={3} className="px-6 py-2.5 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                              Expenses Subtotal
                            </td>
                            <td className="px-6 py-2.5 text-right text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                              {formatCurrency(expenseSubtotal)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* ── Mobile Submit (visible on small screens) ─────────────── */}
            <div className="lg:hidden">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Invoice Total</span>
                  <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(totalAmount)}</span>
                </div>
                <div className="flex gap-3">
                  <Link
                    to="/billing/invoices"
                    className="flex-1 text-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={loading || !canSubmit}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {loading ? 'Creating...' : 'Create Invoice'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* ─── Right Sidebar: Invoice Summary (Sticky) ────────────────── */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div className="sticky top-6 space-y-4">
            {/* Summary Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/80">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  Invoice Summary
                </h4>
              </div>
              <div className="p-5 space-y-4">
                {/* Client */}
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Client</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : '—'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Issue Date</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(formData.issue_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Due Date</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(formData.due_date)}</p>
                  </div>
                </div>

                <hr className="border-gray-100 dark:border-gray-700" />

                {/* Line Items Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Time ({selectedTimeEntries.length} item{selectedTimeEntries.length !== 1 ? 's' : ''})
                    </span>
                    <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">{formatCurrency(timeSubtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Expenses ({selectedExpenses.length} item{selectedExpenses.length !== 1 ? 's' : ''})
                    </span>
                    <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">{formatCurrency(expenseSubtotal)}</span>
                  </div>
                </div>

                <hr className="border-gray-100 dark:border-gray-700" />

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Total</span>
                  <span className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                type="submit"
                form="invoice-form-noop"
                onClick={handleSubmit}
                disabled={loading || !canSubmit}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors shadow-sm"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Invoice...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-4 w-4" />
                    Create Invoice
                  </>
                )}
              </button>
              <Link
                to="/billing/invoices"
                className="w-full inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </Link>
            </div>

            {/* Help Tip */}
            {!canSubmit && formData.client && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-3 flex gap-2">
                <InformationCircleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Select at least one time entry or expense to create an invoice.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Quick Add Client Modal ──────────────────────────────────── */}
      <QuickAddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
};

export default CreateInvoice;
